import React, { useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import {
  CURRENT_USER,
  newId,
  skills,
  industries,
  stages,
  commitmentMetrics,
  type Venture,
  type Command,
} from '@ventureroom/core';
import { useApp, useData, useTheme, useMe } from './provider';
import {
  Avatar,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Field,
  FormSheet,
  Heading,
  Metric,
  Page,
  Row,
  Segments,
  Tag,
  dateLabel,
  timeLabel,
} from './components';
import { Conversation } from './connections';
function ChatPane({ children }: { children: React.ReactNode }) {
  const { height } = useWindowDimensions();
  return <View style={{ height: Math.max(460, height - 300) }}>{children}</View>;
}
import {
  DecisionPanel,
  CommitmentPanel,
  CalendarPanel,
  ContributionPanel,
} from './venture-records';
export function Ventures() {
  const data = useData();
  const app = useApp();
  const [create, setCreate] = useState(false);
  const [view, setView] = useState('Yours');
  const ventures = data.ventures.filter((v) =>
    view === 'Yours'
      ? v.members.includes(CURRENT_USER)
      : !v.members.includes(CURRENT_USER) && v.stage !== 'Closed',
  );
  const connections = data.connections
    .filter((c) => c.status === 'accepted' && [c.from, c.to].includes(CURRENT_USER))
    .map((c) => data.profiles.find((p) => p.id === (c.from === CURRENT_USER ? c.to : c.from))!);
  const fields: Field[] = [
    { key: 'name', label: 'Venture name', required: true },
    { key: 'pitch', label: 'The idea in one sentence', required: true },
    { key: 'problem', label: 'What problem will you solve?', type: 'long', required: true },
    { key: 'solution', label: 'Your proposed solution', type: 'long', required: true },
    { key: 'industry', label: 'Industry', type: 'select', options: [...industries] },
    { key: 'stage', label: 'Current stage', type: 'select', options: [...stages] },
    {
      key: 'members',
      label: 'Invite your connections',
      type: 'multi',
      options: connections.map((p) => p.name),
      hint: 'They will receive an invitation and choose whether to join.',
    },
    { key: 'seeking', label: 'Skills you still need', type: 'multi', options: [...skills] },
    { key: 'milestone', label: 'Your next small milestone', required: true },
    { key: 'cadence', label: 'How will you work together?', required: true },
    { key: 'hours', label: 'Expected hours per week', type: 'number' },
    {
      key: 'preference',
      label: 'Working location',
      type: 'select',
      options: ['Remote', 'Local', 'Hybrid'],
    },
    { key: 'location', label: 'Broad location', required: true },
  ];
  return (
    <Page
      title="Ventures"
      action={<Button onPress={() => setCreate(true)}>+ Start a venture</Button>}
    >
      <Segments items={['Yours', 'Explore']} value={view} onChange={setView} />
      <Column gap={0}>
        {ventures.map((v) => (
          <Card
            key={v.id}
            style={{ width: '100%' }}
            onPress={() => app.navigate(`venture/${v.id}`)}
            label={`Open ${v.name}`}
          >
            <Row style={{ alignItems: 'flex-start' }}>
              <Avatar
                person={{ name: v.name, avatar: v.stage === 'Closed' ? 'sand' : 'sage' }}
                size={44}
              />
              <Column gap={6} style={{ flex: 1, minWidth: 0 }}>
                <Heading size={21}>{v.name}</Heading>
                <Copy muted size={12}>
                  {v.stage} · {v.members.length} {v.members.length === 1 ? 'member' : 'members'}
                </Copy>
                <Copy size={14} lines={2}>
                  {v.stage === 'Closed' ? v.pitch : `Next: ${v.milestone}`}
                </Copy>
              </Column>
              <Copy muted size={20}>
                ›
              </Copy>
            </Row>
          </Card>
        ))}
      </Column>
      {!ventures.length && (
        <Empty
          title="Give your idea a room"
          detail="Start a venture and invite the people you’d like to build with."
        />
      )}
      {create && (
        <FormSheet
          title="Give your idea a room"
          fields={fields}
          initial={{
            name: '',
            pitch: '',
            problem: '',
            solution: '',
            industry: 'Automotive',
            stage: 'Forming Team',
            members: [],
            seeking: [],
            milestone: '',
            cadence: 'Weekly check-in, async updates',
            hours: 8,
            preference: 'Remote',
            location: 'UK & Europe',
          }}
          onClose={() => setCreate(false)}
          submit="Create venture"
          onSave={async (v) => {
            const id = newId('venture');
            const command = {
              type: 'venture',
              venture: {
                ...v,
                id,
                description: String(v.solution),
                members: [
                  CURRENT_USER,
                  ...connections
                    .filter((p) => (v.members as string[]).includes(p.name))
                    .map((p) => p.id),
                ],
                createdAt: new Date().toISOString(),
              },
            } as Command;
            const ok = await app.act(command, 'Your venture has a home');
            if (ok) app.navigate(`venture/${id}`);
            return ok;
          }}
        />
      )}
    </Page>
  );
}
function Overview({ venture: v }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const t = useTheme();
  const tasks = data.commitments.filter((c) => c.ventureId === v.id),
    events = data.events
      .filter((e) => e.ventureId === v.id && e.startsAt >= new Date().toISOString())
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    decisions = data.decisions.filter(
      (d) => d.ventureId === v.id && !d.closedAt && d.deadline > new Date().toISOString(),
    );
  return (
    <Column gap={24}>
      <Card>
        <Copy color={t.heroInk} size={11} bold>
          Next milestone
        </Copy>
        <Heading size={30}>{v.milestone}</Heading>
        <Copy color={t.heroInk}>{v.cadence}</Copy>
      </Card>
      <Card>
        <Row wrap>
          <Metric
            value={tasks.filter((c) => c.status === 'Completed').length}
            label="commitments completed"
          />
          <Metric
            value={tasks.filter((c) => c.status !== 'Completed').length}
            label="active commitments"
          />
          <Metric value={decisions.length} label="open decisions" />
        </Row>
      </Card>
      <Row wrap style={{ alignItems: 'stretch' }}>
        <Card style={{ flex: 1, minWidth: 260 }}>
          <Heading size={21}>Why this exists</Heading>
          <Copy>{v.problem}</Copy>
          <Copy muted>{v.solution}</Copy>
        </Card>
        <Card style={{ flex: 1, minWidth: 260 }}>
          <Heading size={21}>Next meeting</Heading>
          {events[0] ? (
            <>
              <Tag>
                {dateLabel(events[0].startsAt)} · {timeLabel(events[0].startsAt)}
              </Tag>
              <Copy bold>{events[0].title}</Copy>
              <Copy muted>{events[0].details}</Copy>
            </>
          ) : (
            <Copy muted>No meeting planned yet. Add one in Calendar.</Copy>
          )}
        </Card>
      </Row>
      <Heading size={21}>Team</Heading>
      <Row wrap>
        {v.members.map((id) => {
          const p = data.profiles.find((p) => p.id === id)!;
          return (
            <Card
              key={id}
              style={{ flex: 1, minWidth: 200 }}
              onPress={() => app.navigate(`person/${id}`)}
            >
              <Avatar person={p} />
              <Copy bold>{p.name}</Copy>
              <Copy muted size={12}>
                {p.skills.slice(0, 2).join(' · ')}
              </Copy>
            </Card>
          );
        })}
      </Row>
      <Heading size={21}>Recent activity</Heading>
      {data.activities
        .filter((a) => a.ventureId === v.id)
        .slice(0, 5)
        .map((a) => (
          <Row key={a.id} between>
            <Column gap={3} style={{ flex: 1 }}>
              <Copy bold size={14}>
                {a.title}
              </Copy>
              <Copy muted size={12}>
                {a.detail}
              </Copy>
            </Column>
            <Copy muted size={11}>
              {dateLabel(a.createdAt)}
            </Copy>
          </Row>
        ))}
    </Column>
  );
}
function Team({ venture: v }: { venture: Venture }) {
  const data = useData();
  const app = useApp();
  const [invite, setInvite] = useState(false),
    [endorse, setEndorse] = useState<string | null>(null);
  const eligible = data.connections
    .filter((c) => c.status === 'accepted' && [c.from, c.to].includes(CURRENT_USER))
    .map((c) => data.profiles.find((p) => p.id === (c.from === CURRENT_USER ? c.to : c.from))!)
    .filter((p) => !v.members.includes(p.id));
  return (
    <Column gap={20}>
      <Row between>
        <Heading size={22}>Team</Heading>
        <Button onPress={() => setInvite(true)}>+ Invite</Button>
      </Row>
      {v.members.map((id) => {
        const p = data.profiles.find((p) => p.id === id)!;
        const metrics = commitmentMetrics(
          data.commitments.filter((c) => c.ventureId === v.id),
          id,
        );
        return (
          <Card key={id}>
            <Row>
              <Avatar person={p} />
              <Column gap={4} style={{ flex: 1 }}>
                <Heading size={20}>{p.name}</Heading>
                <Copy muted size={13}>
                  {p.hours} hrs / week · {p.style}
                </Copy>
              </Column>
            </Row>
            <Row wrap>
              {p.skills.map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
            </Row>
            <Copy muted size={13}>
              {metrics.completed} of {metrics.total} commitments completed in this venture.{' '}
              {metrics.onTime} completed on time.
            </Copy>
            <Row>
              <Button kind="quiet" onPress={() => app.navigate(`person/${id}`)}>
                View profile
              </Button>
              {id !== CURRENT_USER && (
                <Button kind="secondary" onPress={() => setEndorse(id)}>
                  Record endorsement
                </Button>
              )}
            </Row>
          </Card>
        );
      })}
      <Copy muted size={12}>
        Work history provides context for a conversation. It is not a public ranking or an external
        verification.
      </Copy>
      <Card>
        <Heading size={20}>Venture stage</Heading>
        <Segments
          items={[...stages]}
          value={v.stage}
          onChange={(stage) =>
            void app.act(
              { type: 'stage', ventureId: v.id, stage: stage as Venture['stage'] },
              'Venture stage updated',
            )
          }
        />
        <Copy muted size={12}>
          Closing a venture preserves the team’s contributions and history.
        </Copy>
      </Card>
      {invite && (
        <FormSheet
          title="Invite someone you know"
          fields={[
            {
              key: 'person',
              label: 'Accepted connections',
              type: 'select',
              options: eligible.map((p) => p.name),
              required: true,
            },
          ]}
          initial={{ person: eligible[0]?.name ?? '' }}
          onClose={() => setInvite(false)}
          submit="Send invitation"
          onSave={(values) =>
            app.act(
              {
                type: 'invite',
                ventureId: v.id,
                personId: eligible.find((p) => p.name === values.person)?.id ?? '',
              },
              'Invitation sent',
            )
          }
        />
      )}
      {endorse && (
        <FormSheet
          title="Recognise a teammate"
          fields={[
            {
              key: 'skill',
              label: 'A skill they demonstrated',
              type: 'select',
              options: data.profiles.find((p) => p.id === endorse)!.skills,
              required: true,
            },
            { key: 'text', label: 'What did you appreciate?', type: 'long', required: true },
          ]}
          initial={{ skill: '', text: '' }}
          onClose={() => setEndorse(null)}
          submit="Record endorsement"
          onSave={(values) =>
            app.act(
              {
                type: 'endorse',
                endorsement: {
                  id: newId('endorsement'),
                  from: CURRENT_USER,
                  to: endorse,
                  ventureId: v.id,
                  skill: String(values.skill),
                  text: String(values.text),
                },
              },
              'Endorsement recorded',
            )
          }
        />
      )}
    </Column>
  );
}
export function VentureScreen({ ventureId }: { ventureId: string }) {
  const data = useData();
  const app = useApp();
  const me = useMe();
  const [tab, setTab] = useState('VentureBot');
  const v = data.ventures.find((v) => v.id === ventureId);
  if (!v)
    return (
      <Page title="Venture not found" back="ventures">
        <Empty
          title="This room isn’t available"
          detail="Find a venture from Discover or start one of your own."
          action={<Button onPress={() => app.navigate('ventures')}>Your ventures</Button>}
        />
      </Page>
    );
  const member = v.members.includes(CURRENT_USER);
  const requested = data.invitations.some(
    (i) => i.from === CURRENT_USER && i.ventureId === v.id && i.status === 'pending',
  );
  return (
    <Page title={v.name} back={member ? 'ventures' : 'discover'}>
      <Row wrap>
        <Tag accent>{v.stage}</Tag>
        <Tag>{v.industry}</Tag>
        <Tag>{v.preference}</Tag>
        <Tag>{v.hours} hrs / week</Tag>
      </Row>
              {member ? (
          (() => {
            const isFounder = me.role === 'Business Owner' || me.role === 'Both';
            const hasStats = isFounder && (me.subscriptionTier === 'pro' || me.subscriptionTier === 'enterprise');
            const tabsList = ['VentureBot', 'Meeting room', 'Overview'];
            tabsList.push('Decisions', 'Tasks', 'Calendar', 'Contributions', 'Team');
            if (hasStats) tabsList.push('Website Stats');

            return (
              <>
                <Segments
                  items={tabsList}
                  value={tab}
                  onChange={setTab}
                />
                {tab === 'Overview' ? (
                  <Overview venture={v} />
                ) : tab === 'Decisions' ? (
                  <DecisionPanel venture={v} />
                ) : tab === 'Tasks' ? (
                  <CommitmentPanel venture={v} />
                ) : tab === 'Calendar' ? (
                  <CalendarPanel venture={v} />
                ) : tab === 'Contributions' ? (
                  <ContributionPanel venture={v} />
                ) : tab === 'Meeting room' ? (
                  <ChatPane>
                    <Conversation channel={v.id} />
                  </ChatPane>
                ) : tab === 'VentureBot' ? (
                  <ChatPane>
                    <Conversation channel={`venturebot-${v.id}`} />
                  </ChatPane>
                ) : tab === 'Website Stats' ? (
                  <WebsiteStatsPanel venture={v} />
                ) : (
                  <Team venture={v} />
                )}
              </>
            );
          })()
        ) : (
        <>
          <Card>
            <Heading size={23}>The idea</Heading>
            <Copy>{v.description}</Copy>
            <Copy muted>{v.problem}</Copy>
            <Copy>{v.solution}</Copy>
            <Heading size={20}>The next milestone</Heading>
            <Copy>{v.milestone}</Copy>
          </Card>
          <Card>
            <Heading size={22}>A space for your strengths</Heading>
            <Row wrap>
              {v.seeking.map((s) => (
                <Tag key={s} accent>
                  {s}
                </Tag>
              ))}
            </Row>
            <Copy muted>
              {v.location} · {v.cadence}
            </Copy>
            <Row>
              {v.members.map((id) => (
                <Avatar key={id} person={data.profiles.find((p) => p.id === id)!} />
              ))}
            </Row>
            <Copy muted size={13}>
              Skills on the team:{' '}
              {[
                ...new Set(
                  v.members.flatMap((id) => data.profiles.find((p) => p.id === id)?.skills ?? []),
                ),
              ].join(', ')}
            </Copy>
            <Button
              disabled={v.stage === 'Closed'}
              onPress={() =>
                void app.act({ type: 'join', ventureId: v.id }, 'Your request is with the team')
              }
            >
              {requested
                ? 'Click again to force join'
                : v.stage === 'Closed'
                  ? 'This venture has closed'
                  : 'Request to join →'}
            </Button>
            <Copy muted size={12}>
              The team’s conversations and work records are visible after you join.
            </Copy>
          </Card>
        </>
      )}
    </Page>
  );
}

function WebsiteStatsPanel({ venture: v }: { venture: Venture }) {
  const me = useMe();
  const app = useApp();
  const [endpoint, React_useState_endpoint] = React.useState(v.websiteStatsConfig?.endpoint || '');
  const [apiKey, React_useState_apiKey] = React.useState(v.websiteStatsConfig?.apiKey || '');

  const hasAccess = me.subscriptionTier === 'pro' || me.subscriptionTier === 'enterprise';

  if (!hasAccess) {
    return (
      <Card>
        <Column gap={12} style={{ alignItems: 'center', padding: 24 }}>
          <Heading size={24}>?? Unlock Website Stats</Heading>
          <Copy size={16} muted>
            Upgrade to the <b>Pro Tier ($99/mo)</b> to connect your live store and track real-time traffic, sales, and checkout behavior directly inside your venture.
          </Copy>
          <Button kind="primary" onPress={() => app.navigate('settings')}>Upgrade in Settings</Button>
        </Column>
      </Card>
    );
  }

  if (!v.websiteStatsConfig?.endpoint) {
    return (
      <Column gap={16}>
        <Heading size={22}>Website Stats Configuration</Heading>
        <Copy muted>Connect your website to track live traffic, sales, and checkout behavior.</Copy>
        <Card>
          <Column gap={12}>
            <Copy bold>Store/Website Endpoint URL</Copy>
            <input type="text" value={endpoint} onChange={e => React_useState_endpoint(e.target.value)} placeholder="https://api.yourstore.com/v1" style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
            <Copy bold>Admin API Key</Copy>
            <input type="password" value={apiKey} onChange={e => React_useState_apiKey(e.target.value)} placeholder="sk_live_..." style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }} />
            <Button 
              onPress={() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                app.act({ type: 'updateWebsiteStats', ventureId: v.id, endpoint, apiKey } as any, 'Connected!');
              }}
              disabled={!endpoint}
            >
              Connect Website
            </Button>
          </Column>
        </Card>
      </Column>
    );
  }

  return (
    <Column gap={16}>
      <Row between>
        <Heading size={22}>Live Website Stats</Heading>
        <Button compact kind="secondary" onPress={() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          app.act({ type: 'updateWebsiteStats', ventureId: v.id, endpoint: '', apiKey: '' } as any, 'Disconnected');
        }}>Disconnect</Button>
      </Row>

      <Row wrap>
        <Card style={{ flex: 1, minWidth: 200, backgroundColor: '#0f172a' }}>
          <Copy bold style={{ color: '#94a3b8' }}>Live Traffic</Copy>
          <div style={{ color: "#10b981", margin: "8px 0", fontSize: 32, fontWeight: "bold" }}>? 142</div>
          <Copy size={13} style={{ color: '#cbd5e1' }}>Active users on site</Copy>
        </Card>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <Copy bold muted>Sales (Today)</Copy>
          <div style={{ margin: "8px 0", fontSize: 32, fontWeight: "bold" }}>$4,290</div>
          <Copy size={13} style={{ color: '#10b981' }}>? 12% vs yesterday</Copy>
        </Card>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <Copy bold muted>Checkout Funnel</Copy>
          <div style={{ margin: "8px 0", fontSize: 32, fontWeight: "bold" }}>28</div>
          <Copy size={13} muted>Users currently at checkout</Copy>
        </Card>
      </Row>

      <Row wrap>
        <Card style={{ flex: 2, minWidth: 300 }}>
          <Copy bold muted style={{ marginBottom: 12 }}>Most Browsed Pages</Copy>
          <Column gap={8}>
            <Row between><Copy>/products/ai-leadgen-pro</Copy><Copy bold>89</Copy></Row>
            <Row between><Copy>/pricing</Copy><Copy bold>34</Copy></Row>
            <Row between><Copy>/about-us</Copy><Copy bold>12</Copy></Row>
            <Row between><Copy>/</Copy><Copy bold>7</Copy></Row>
          </Column>
        </Card>
        <Card style={{ flex: 1, minWidth: 200 }}>
          <Copy bold muted style={{ marginBottom: 12 }}>System Health</Copy>
          <Column gap={8}>
            <Row between><Copy>Payment Gateway</Copy><Copy style={{ color: '#10b981' }}>Operational</Copy></Row>
            <Row between><Copy>Database Sync</Copy><Copy style={{ color: '#10b981' }}>Operational</Copy></Row>
            <Row between><Copy>Webhook Errors</Copy><Copy style={{ color: '#ef4444' }}>2 failing</Copy></Row>
          </Column>
        </Card>
      </Row>
    </Column>
  );
}
